import React, { useEffect, useState } from "react";
import { ReceiptText, ExternalLink, Loader2 } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import api from "../../api";

const money = (n, cur) => `${cur === "USD" || !cur ? "$" : ""}${Number(n || 0).toFixed(2)}${cur && cur !== "USD" ? " " + cur : ""}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

const statusStyle = (s) => s === "paid"
  ? "bg-[#e7f6ec] text-[#0e6b3e]"
  : s === "refunded" ? "bg-rose-50 text-rose-600 dark:bg-rose-950/40"
  : "bg-secondary text-muted-foreground";

// A shop's own paper trail: every charge, its unique receipt code and a link to the Stripe invoice.
export const BillingHistory = () => {
  const { t } = useLang();
  const [rows, setRows] = useState(null);

  useEffect(() => { api.get("/my/receipts").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);

  if (rows === null) {
    return (
      <div className="bg-card border border-border rounded-2xl p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="billing-history-loading">
        <Loader2 size={15} className="animate-spin" /> {t("bh.loading")}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="billing-history">
      <div>
        <h2 className="font-bold text-foreground flex items-center gap-2">
          <ReceiptText size={18} className="text-[#16a34a]" /> {t("bh.title")}
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t("bh.subtitle")}</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="billing-history-empty">{t("bh.empty")}</p>
      ) : (
        <div className="space-y-2" data-testid="billing-history-list">
          {rows.map((r, i) => (
            <div key={r.receiptCode || i} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border border-border/70 rounded-xl px-3 py-2.5" data-testid={`receipt-${r.receiptCode || i}`}>
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold text-foreground" data-testid={`receipt-code-${i}`}>{r.receiptCode || "—"}</p>
                <p className="text-[11px] text-muted-foreground">
                  {fmtDate(r.createdAt)} · {r.packageId || r.type}
                  {r.invoiceNumber ? ` · ${r.invoiceNumber}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-bold text-foreground text-sm">{money(r.amount, r.currency)}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusStyle(r.status)}`}>
                  {r.status === "pending" ? t("bh.pending") : r.status === "refunded" ? t("bh.refunded") : t("bh.paid")}
                </span>
                {r.invoiceUrl && (
                  <a href={r.invoiceUrl} target="_blank" rel="noreferrer" data-testid={`receipt-invoice-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#16a34a] hover:underline">
                    {t("bh.invoice")} <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BillingHistory;
